import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import s from "./activation.module.css";
import { useActivation } from "./ActivationContext";

const PAD = 6;

function measure(selector) {
  if (!selector) return null;
  const el =
    document.querySelector(`${selector}[data-act-active="1"]`) ||
    document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return {
    top: Math.max(0, r.top - PAD),
    left: Math.max(0, r.left - PAD),
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
  };
}

/**
 * Soft dim overlay with rounded spotlight hole (Appcues / Intercom style).
 * pointer-events: none — user clicks the real control.
 */
export default function ActivationSpotlight() {
  const { visible, currentSubstep, collapsed } = useActivation();
  const [hole, setHole] = useState(null);

  // Nav hints: highlight menu item only — no full-page dim (breaks sidebar look).
  const selector =
    currentSubstep?.kind === "nav" ? null : currentSubstep?.target || null;

  const remeasure = useCallback(() => {
    setHole(measure(selector));
  }, [selector]);

  useEffect(() => {
    if (!visible || collapsed || !selector) {
      setHole(null);
      return undefined;
    }
    remeasure();
    const el =
      document.querySelector(`${selector}[data-act-active="1"]`) ||
      document.querySelector(selector);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        remeasure();
      }, 100);
    }
    const t = window.setInterval(remeasure, 250);
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [visible, collapsed, selector, remeasure]);

  if (!visible || collapsed || !hole) return null;

  return createPortal(
    <div className={s.actSpotRoot} aria-hidden>
      <div
        className={s.actSpotHole}
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
        }}
      />
    </div>,
    document.body,
  );
}
